import { FC, KeyboardEvent, useMemo, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import { useConditionalEffect } from 'react-delta';

import * as K from 'common/constants';
import ELEMENT from 'common/element';
import { HELP_ROUTE } from 'components/help/constants';
import { useIsNewSearch } from 'hooks/useIsNewSearch';
import { PlotData, PqResultData, QueryType, ResultType } from 'common/graphqlTypes';
import { truncateText } from 'common/util/string';
import { tokenizePowerQuery } from 'common/util/filter/command';
import { TOKEN_TYPE } from 'common/util/filter/types';
import { formatPowerQueryResults } from 'common/util/format';
import { getDownloadFilename } from 'common/util/file';
import { downloadFile } from 'service/browser';
import Page from 'components/common/Page/Page';
import LoadingPlaceholder from 'components/common/LoadingPlaceholder/LoadingPlaceholder';
import ErrorPlaceholder from 'components/common/ErrorPlaceholder/ErrorPlaceholder';
import SearchCanceledPlaceholder from 'components/common/SearchCanceledPlaceholder/SearchCanceledPlaceholder';
import {
  absoluteResolvedUrlState,
  completePowerQueryState,
  queryTimeRangeState,
  searchParamState,
  teamEmailsState,
} from 'components/common/Managers/RouteStateManager.state';
import { dataDetailsCardTriggerState } from 'components/common/Managers/ViewStateManager.state';

import log from 'common/ScalyrLogger';
import useLRQ, {
  ErrorResult,
  generatePQRequest,
  LRQRequest,
  SuccessResult,
  UseLRQReturnValue,
} from 'hooks/useLRQ/useLRQ';
import { useLRQIndication } from 'components/common/Managers/LRQRequestManager.hooks';
import { timezoneState } from 'components/common/Managers/PreferencesManager.state';
import useCopyToClipboard from 'hooks/useCopyToClipboard';
import { networkErrorState } from 'api/apolloClient.state';
import { useShortUrl } from 'hooks/useShortUrl';

import PowerQueriesContent from './components/PowerQueriesContent/PowerQueriesContent';
import PowerQueryControls from './components/PowerQueryControls';
import { removeColumns } from './helpers';

import './styles.scss';

type UsePQLRQResultsReturn = {
  status: K.QueryStatus;
  error: ErrorResult | null;
  result: PqResultData;
  plotResult: PlotData;
};

export type CopyToClipboardHandler = (format: K.DATA_FORMAT, type?: string, addShortUrl?: boolean) => void;
export type DownloadFileHandler = (format: K.DATA_FORMAT, addShortUrl?: boolean) => void;

// #region usePQLRQResults
function isVariablesSettled(request: LRQRequest): boolean {
  let isSettled = true;

  request.queriesVariables.some((queryVariables) => {
    /* 1: if query filter is empty, we do not need to fire an LRQ (e.g. on initial page load) */
    if (queryVariables?.filter === '') {
      isSettled = false;
      log.info(`LRQ won't be launched \n 'filter' is empty \n LRQ ID: ${queryVariables.id}`);

      return true; // stop searching further
    }

    /* 2: if query defines the `startTime` variable, but it's not yet initialized – we shouldn't fire an LRQ at that moment */
    if (queryVariables?.startTime === '') {
      isSettled = false;
      log.info(`LRQ won't be launched \n 'startTime' is not yet settled \n LRQ ID: ${queryVariables.id}`);

      return true; // stop searching further, one missing variable is enough
    }

    return false; // continue to search
  });

  return isSettled;
}

function getLRQStatus(lrqState: UseLRQReturnValue, skip: boolean): K.QueryStatus {
  let status = (lrqState.result.status as K.QueryStatus) || K.QueryStatus.Running;
  if (skip) {
    status = K.QueryStatus.Done;
  }

  return status;
}

function usePQLRQResults(request: LRQRequest): UsePQLRQResultsReturn {
  const skip = !isVariablesSettled(request);
  const lrqState = useLRQ(request, { fetchPolicy: 'no-cache', skip });
  const status = getLRQStatus(lrqState, skip);

  // 'DONE' -> result
  const result = (lrqState.result as SuccessResult)?.results?.[0]?.data || {};

  // 'TERMINATED' -> error
  let error: ErrorResult | null = null;
  if (status === K.QueryStatus.Terminated) {
    error = lrqState.result as ErrorResult;
  }

  // Visualize LRQ progress
  useLRQIndication(lrqState, request);

  // Result object signature differs if request was for PlotData. We copy results into separate
  // variables so we can access different properties without hurting typescript's feelings.
  const plotResult = request?.queriesVariables?.[0].powerQuery?.resultType === ResultType.PLOT ? result : undefined;
  return { status, error, result, plotResult };
}
// #endregion usePQLRQResults

const PowerQueriesPage: FC = () => {
  const isNewSearch = useIsNewSearch();
  const queryTimeRange = useRecoilValue(queryTimeRangeState);
  const filter = useRecoilValue(searchParamState(K.URL_PARAM.FILTER));
  const { powerQuery: completePowerQuery, addedColumns } = useRecoilValue(completePowerQueryState);
  const graphStyle = useRecoilValue(searchParamState(K.URL_PARAM.GRAPH_STYLE));
  const teamEmails = useRecoilValue(teamEmailsState);
  const { startTime, endTime, startTimeNs, endTimeNs, requestTime } = queryTimeRange;
  const absoluteUrl = useRecoilValue(absoluteResolvedUrlState);
  const { fetchShortUrlPromise } = useShortUrl();
  const copy = useCopyToClipboard();
  const [dataDetailsFieldTrigger, setDataDetailsFieldTrigger] = useRecoilState(dataDetailsCardTriggerState);

  const title = truncateText(completePowerQuery, K.TITLE_MAX_LENGTH) || 'Power Query';
  const timezone = useRecoilValue(timezoneState);
  const isPlotReq = graphStyle === K.CHART_TYPES.LINE || graphStyle === K.CHART_TYPES.STACKED_BAR;

  let adjustedPowerQuery = completePowerQuery;
  let isLimitAdded = false;
  const tokens = tokenizePowerQuery(completePowerQuery);
  if (
    completePowerQuery !== '' &&
    !tokens.find((token) => token.type === TOKEN_TYPE.COMMAND && K.PQ_LIMIT_COMMANDS.includes(token.text))
  ) {
    adjustedPowerQuery = `${completePowerQuery}|limit ${K.PQ_DEFAULT_LIMIT}`;
    isLimitAdded = true;
  }

  const requestFromUrl = generatePQRequest({
    startTime: startTimeNs,
    endTime: endTimeNs,
    filter: adjustedPowerQuery,
    requestTime,
    teamEmails,
    resultType: isPlotReq ? QueryType.PLOT : undefined,
  });
  const networkError = useRecoilValue(networkErrorState);
  const [request, setRequest] = useState(requestFromUrl);

  // TODO: adding 'cells' field for chart views, but we probably should migrate standard table view to 'cells'.
  // 'values' is marked as deprecated.
  const { status: lrqStatus, error, result, plotResult } = usePQLRQResults(request);

  const {
    columns = [],
    cells = [],
    values = [],
    matchCount = 0,
    omittedEvents = 0,
    partialResultsDueToTimeLimit = false,
  } = result;

  // this effect serves as the PQ state manager (PQ should probably have a separate state manager like other search pages)
  useConditionalEffect(() => {
    log.debug(
      'New PQ search for "' +
        adjustedPowerQuery +
        '" , from ' +
        new Date(Number(startTime)) +
        ' to ' +
        new Date(Number(endTime)),
    );
    setRequest(requestFromUrl);
  }, isNewSearch);

  const errorMessages: Record<string, string> = useMemo(() => {
    const graphQLError: Record<string, string>[] = error?.terminationReasonsDetails?.[0] || [];
    let querySyntaxError;

    if (Array.isArray(graphQLError) && graphQLError.length > 0) {
      querySyntaxError = graphQLError.reduce(
        (accumulator: Record<string, string>, currentValue: Record<string, string>) => ({
          ...accumulator,
          [`${currentValue.field}`]: currentValue.message,
        }),
        {},
      );
    }

    // If querySyntxError object contains properties - errorPosition, errorLength, message then it is a syntax error, otherwise we return the top error message
    return querySyntaxError?.errorPosition && querySyntaxError?.errorLength && querySyntaxError?.message
      ? querySyntaxError
      : { genericErrorMessage: error?.terminationReasons?.[0] || '' };
  }, [error]);

  const copyToClipboardHandler = (format: K.DATA_FORMAT, type = 'value', addShortUrl = false) => {
    const options = { timezone, filter };
    const { columns: updatedColumns, values: updatedValues } = removeColumns(columns, values, addedColumns);
    if (addShortUrl) {
      fetchShortUrlPromise(absoluteUrl).then(({ data }) => {
        const content = formatPowerQueryResults(updatedColumns, updatedValues, format, {
          ...options,
          shortUrl: data?.compressUrl,
        });
        copy(content, type);
      });
    } else {
      const content = formatPowerQueryResults(updatedColumns, updatedValues, format, options);
      copy(content, type);
    }
  };

  const downloadFileHandler = (format: K.DATA_FORMAT, addShortUrl = false) => {
    const { columns: updatedColumns, values: updatedValues } = removeColumns(columns, values, addedColumns);
    const fileName = getDownloadFilename(format, true);
    const contentType = K.DATA_FORMAT_CONTENT_TYPE[format];

    if (addShortUrl) {
      fetchShortUrlPromise(absoluteUrl).then(({ data }) => {
        const content = formatPowerQueryResults(updatedColumns, updatedValues, format, {
          timezone,
          filter,
          shortUrl: data?.compressUrl,
        });
        downloadFile({ content, fileName, contentType });
      });
    } else {
      const content = formatPowerQueryResults(updatedColumns, updatedValues, format, { timezone, filter });
      downloadFile({ content, fileName, contentType });
    }
  };

  const closeDrawer = (): void => setDataDetailsFieldTrigger(null);

  function renderResultsArea(): JSX.Element {
    if (networkError) {
      return (
        <div data-test-id={ELEMENT.powerQueryNetworkErrorPlaceholder}>
          <h3 className="PowerQueries__InvalidTextHeader">There was an network error while performing your request</h3>
          <div className="PowerQueries__InvalidTextDescription">↳Please check your internet connection</div>
        </div>
      );
    }

    if (request.canceled) {
      return (
        <SearchCanceledPlaceholder
          message="Search Canceled"
          containerClassName={['SearchCanceledPlaceholder__Container--WithPadding']}
        />
      );
    }

    if (lrqStatus === K.QueryStatus.Running) {
      return <LoadingPlaceholder offsetTop={0} />;
    }

    if (errorMessages.genericErrorMessage) {
      let errorMessage = errorMessages.genericErrorMessage;
      let errorLink;
      if (graphStyle) {
        errorLink = {
          url: HELP_ROUTE.HELP_POWER_QUERIES_FUNCTIONS, // TODO: Once help in react is released, link should point to PQ chart doc route.
          text: 'Help with Power Query functions.',
          openInNewTab: true,
        };
        errorMessage = errorMessage + '. Switch to Table view, or add a timebucket() function to a Line or Bar chart.';
      }
      return <ErrorPlaceholder error={{ title: 'Invalid Search', message: errorMessage, errorLink }} />;
    }

    return (
      <PowerQueriesContent
        error={!!error}
        errorMessages={errorMessages}
        sendQuery={filter}
        columns={columns}
        values={values}
        cells={cells}
        matchCount={matchCount}
        plotResult={plotResult}
        omittedEvents={omittedEvents}
        partialResultsDueToTimeLimit={partialResultsDueToTimeLimit}
        isLimitAdded={isLimitAdded}
        isDrawerOpen={!!dataDetailsFieldTrigger}
        closeDrawer={closeDrawer}
      />
    );
  }

  // Close drawer on Esc
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const key = event.key as K.KEYBOARD_KEY;
    if (key === K.KEYBOARD_KEY.ESCAPE) {
      closeDrawer();
    }
  };

  return (
    <Page docTitle={title} pageModifier="PowerQueries" testId={ELEMENT.powerQueryPage}>
      <div className="PowerQueries__InputArea">
        <PowerQueryControls
          copyToClipboardHandler={copyToClipboardHandler}
          downloadFileHandler={downloadFileHandler}
          disableCopyAndDownload={values.length === 0}
        />
      </div>
      <div className="PowerQueries__ResultsArea" onKeyDown={handleKeyDown} role="main" tabIndex={-1}>
        {renderResultsArea()}
      </div>
    </Page>
  );
};

export default PowerQueriesPage;
